// Shared pieces for the identity pages: the branded card, the manual authenticator key (no QR
// renderer is installed, and no third-party QR service is ever used) and the code field.
import { type ReactNode, useEffect } from 'react';
import { APP_NAME, BRAND_ASSETS, windowTitle } from '../config/brand.ts';
import { BrandImage } from '../ui/Wordmark.tsx';
import { Diagnostics } from '../ui/Diagnostics.tsx';
import type { FlowDiagnostic } from './flow.ts';
import type { PreparedMfa } from './contract.ts';

export function IdentityFrame({ title, section, children, foot }: { title: string; section: string; children: ReactNode; foot?: ReactNode }) {
  useEffect(() => { document.title = windowTitle(section); }, [section]);
  return (
    <main id="main" className="auth-page">
      <div className="auth-card identity-card">
        <p className="auth-brand"><BrandImage asset={BRAND_ASSETS.lockup} height={32} /></p>
        <h1 className="auth-title">{title}</h1>
        {children}
        {foot === undefined ? <p className="auth-foot">Access is by invitation. There is no public sign-up.</p> : foot}
      </div>
    </main>
  );
}

/**
 * After a step that used an authenticator code (joining with two-step verification, turning it
 * on, or a reset that needed a code). The server refuses that code from now on (replay
 * protection), so the next sign-in needs the app's next code.
 */
export const NEW_CODE_HEADLINE = 'Verified. Sign in with a new authenticator code.';
export const NEW_CODE_DETAIL = 'The code you just entered can’t be used again. Wait for your authenticator app to show its next code.';

export function NewCodeNotice() {
  return <p className="notice notice-info"><strong>{NEW_CODE_HEADLINE}</strong> {NEW_CODE_DETAIL}</p>;
}

export function FlowDiagnostics({ diagnostic }: { diagnostic: FlowDiagnostic | null }) {
  return diagnostic ? <Diagnostics diagnostic={{ code: diagnostic.code, detail: null, requestId: diagnostic.requestId }} /> : null;
}

/** "ABCD EFGH …": easier to type; authenticator apps ignore the spaces. */
export function groupKey(secret: string): string {
  return secret.replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * The intentional enrollment view's key. Shown only while enrolling; the parent drops it on
 * cancel, expiry, account change and success. Never logged, stored or put in a link.
 */
export function AuthenticatorKey({ mfa }: { mfa: PreparedMfa }) {
  if (!mfa.secret) return null;
  return (
    <div className="totp-key">
      <ol className="totp-steps">
        <li>In your authenticator app, add an account and choose to enter a setup key.</li>
        <li>Enter this key exactly. Choose time-based if the app asks.</li>
        <li>Type the 6-digit code the app then shows for {APP_NAME}.</li>
      </ol>
      <dl className="auth-facts">
        <div><dt>Account</dt><dd>{mfa.account}</dd></div>
        <div><dt>Setup key</dt><dd><span className="mono totp-seed">{groupKey(mfa.secret)}</span></dd></div>
      </dl>
      <p className="meta">Keep this key private: anyone with it can make your codes. This page shows no QR code, so the key is entered by hand.</p>
    </div>
  );
}

export function CodeField({ id, value, onChange, label = 'Verification code', hint, error, optional, autoFocus }: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  hint?: string;
  error?: boolean;
  optional?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}{optional ? <span className="optional"> (if you use one)</span> : null}</label>
      {hint ? <p className="field-hint" id={`${id}-hint`}>{hint}</p> : null}
      <input
        id={id}
        name="one-time-code"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        autoFocus={autoFocus}
        aria-describedby={hint ? `${id}-hint` : undefined}
        aria-invalid={error ? true : undefined}
        value={value}
        onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      />
    </div>
  );
}

export function PasswordField({ id, label, value, onChange, autoComplete, hint, autoFocus }: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  hint?: string;
  autoFocus?: boolean;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {hint ? <p className="field-hint" id={`${id}-hint`}>{hint}</p> : null}
      <input id={id} type="password" autoComplete={autoComplete} autoFocus={autoFocus} aria-describedby={hint ? `${id}-hint` : undefined} value={value} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

export function FieldError({ id, message }: { id: string; message: string | null }) {
  return message ? <p id={id} className="form-error" role="alert">{message}</p> : null;
}
